import { Capacitor } from '@capacitor/core'
import { ColoursObj } from '@eventr/common-storybook'
import useEmblaCarousel from 'embla-carousel-react'
import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { useLocation } from 'react-router'
import { useShallow } from 'zustand/react/shallow'
import {
    setStatusBarStyleDark,
    setStatusBarStyleLight,
} from '../../../feat/appState/statusBar'
import { Paths } from '../../../routes'
import { bs, useBoundStore } from '../../../state/bound.store'
import { homeTabSignal } from '../../../state/itineraryState/itinerarySignals'
import { useScrollContext } from '../../../state/scrollState/scrollContext'
import { scrollRef } from '../../../state/scrollState/scrollSignals'
import { useEphemeralStore } from '../../../state/useEphemeralStore'
import { windowSizes } from '../../../state/windowState/windowSignals'
import { CarouselContainer } from './CarouselContainer'
import { CarouselFooter } from './CarouselFooter'
import { CarouselOverlay } from './CarouselOverlay'
import { CarouselSlide } from './CarouselSlide'
import { SlideIterator } from './slide/SlideIterator'
/**
 * A carousel of itineraries in the selected organisation at the top of the
 * Home, Chat, and Team pages. Supports swiping left and right to select an
 * itinerary in the carousel. Each slide has a card which expands to show
 * top-level information about the itinerary (e.g., address, website), as well
 * as location sharing and email booking service functionality.
 *
 * Strategies:
 * - The carousel uses Embla carousel (https://www.embla-carousel.com/).
 * - The carousel removes the top safe area when mounted and restores it when
 *   unmounted.
 * - The carousel adjusts its height based on the selected itinerary and the
 *   open/close state. This logic is based on the auto-height plugin
 *   (https://www.embla-carousel.com/plugins/auto-height/), but **does not use
 *   it**, as it is not extensible with extra dependencies (namely
 *   opening/closing the slide cards)
 *
 * Todo:
 * - Combine the two itinerary endpoints into one to reduce rendering overhead
 *   caused by needing to merge and filter data.
 * @returns The carousel of itineraries.
 */

export default function ItineraryCarousel() {
    const { selectedItinID } = useBoundStore(
        useShallow((s) => {
            return {
                selectedItinID: s.selectedItinID,
            }
        })
    )
    const itineraryMap = useCachedItineraryMap()
    const { headerHeight } = useScrollContext(
        useShallow((s) => ({
            headerHeight: s.headerHeight,
        }))
    )

    // MARK: CAROUSEL SETUP
    /**
     * Setting up the carousel.
     *
     * Documentation reference: https://www.embla-carousel.com/get-started/react/#accessing-the-carousel-api
     */

    const startIndex = useMemo(() => {
        if (itineraryMap.length === 0) return 0
        return useBoundStore
            .getState()
            .itineraryMap.findIndex((itin) => itin?.id == bs().selectedItinID)
    }, [itineraryMap.length])

    const carouselOptions = useMemo(
        () => ({
            loop: true,
            duration: 15,
            watchDrag: itineraryMap.length > 1,
            startIndex: startIndex,
        }),
        [itineraryMap.length, startIndex]
    )

    const [carouselRef, carouselObserver] = useEmblaCarousel(carouselOptions)
    /**
     * Updates the slides in view when the carousel is scrolled. This is used to
     * implement lazy loading for the background images.
     */
    const updateSlidesInView = useCallback(
        (carouselObserver: any) => {
            setSlidesInView((slidesInView) => {
                if (
                    slidesInView.length === carouselObserver.slideNodes().length
                ) {
                    carouselObserver.off('slidesInView', updateSlidesInView)
                }
                const inView = carouselObserver
                    .slidesInView()
                    .filter((index: any) => !slidesInView.includes(index))
                return slidesInView.concat(inView)
            })
        },
        [carouselObserver]
    )

    useEffect(() => {
        if (!carouselObserver) return

        if (
            itineraryMap?.at(carouselObserver.selectedScrollSnap())?.id !=
            bs().selectedItinID
        ) {
            carouselObserver.scrollTo(
                itineraryMap.findIndex((s) => s.id == bs().selectedItinID)
            )
        }
    }, [itineraryMap.length, selectedItinID])

    /**
     * Sets up the event listeners for the carousel.
     */
    useEffect(() => {
        if (!carouselObserver) return

        carouselObserver.on('pointerDown', () => setIsScrolling(true))
        carouselObserver.on('pointerUp', () => setIsScrolling(false))
        carouselObserver.on('slidesInView', (carousel) => {
            setSlidesInView(carousel.slidesInView())
        })
        carouselObserver.on('select', (carousel) => {
            bs().setCurrentItin(
                bs().itineraryMap?.at(carousel.selectedScrollSnap())?.id ?? -1
            )
        })

        // Lazy loading logic to update slides in view.
        updateSlidesInView(carouselObserver)
        carouselObserver.on('slidesInView', updateSlidesInView)

        return function cleanup() {
            carouselObserver.destroy()
        }
    }, [carouselObserver, updateSlidesInView])

    // MARK: SLIDE STATE
    /**
     * The index of selected slide in the carousel. Used to determine the
     * selected itinerary ID.
     */

    const [slidesInView, setSlidesInView] = useState<number[]>([])

    /**
     * Scrolls to the previous slide in the carousel.
     */
    const scrollPrev = useCallback(() => {
        if (carouselObserver) carouselObserver.scrollPrev()
    }, [carouselObserver])

    /**
     * Scrolls to the next slide in the carousel.
     */
    const scrollNext = useCallback(() => {
        if (carouselObserver) carouselObserver.scrollNext()
    }, [carouselObserver])

    /**
     * Whether the carousel is currently scrolling. Used to hide the previous
     * and next buttons during scrolling.
     */
    const [isScrolling, setIsScrolling] = useState(false)

    // MARK: HEADER SIZING
    /**
     * Used to find the current URL path, for differentiating between Home,
     * Chat, and Team pages.
     */
    const { pathname } = useLocation()

    /**
     * A map of the slide cards in the carousel, used to calculate the header
     * height. These slide cards can vary in height as itineraries may differ in
     * the amount of information they display.
     */
    const headerCardReferences = useRef<Map<string, HTMLElement> | null>(null)

    /**
     * Returns the map of slide cards in the carousel.
     */
    const getHeaderCardMap = useCallback(() => {
        // If the map does not exist, create it.
        if (!headerCardReferences.current) {
            headerCardReferences.current = new Map()
        }

        // Otherwise, return the map.
        return headerCardReferences.current
    }, [headerCardReferences.current])

    /**
     * Adds or removes a slide card reference to the map of slide cards.
     */
    const handleSlideCardRef = useCallback(
        (node: any, index: any) => {
            const map = getHeaderCardMap()

            if (node) {
                if (map) {
                    map.set(index.toString(), node)
                }
            } else {
                if (map) {
                    map.delete(index.toString())
                }
            }
        },
        [getHeaderCardMap]
    )

    // MARK: REMOVING TOP SAFE AREA
    // Uses the Mobile App Frame reference to change the grid layout to set the
    // top safe area to 0px (effectively removing it) when the carousel is
    // mounted. On unmount, return the grid layout to the original template.

    const resetTemplate = useCallback(() => {
        const components: string[] = [
            `${windowSizes.value.insets?.insets?.top ?? 0}px`,
            '1fr',
            `${windowSizes.value.insets?.insets.bottom ?? 0}px`,
        ]
        if (scrollRef?.value?.current?.style)
            scrollRef.value.current.style.gridTemplateRows =
                components.join(' ')
        if (!Capacitor.isNativePlatform()) return
        if (ColoursObj.navbar.light) {
            setStatusBarStyleDark()
        } else {
            setStatusBarStyleLight()
        }
    }, [scrollRef])

    const { mapIsFullscreen } = useEphemeralStore(
        useShallow((s) => {
            return {
                mapIsFullscreen: s.mapIsFullscreen,
            }
        })
    )

    const expandMap = useMemo(() => {
        return (
            mapIsFullscreen &&
            pathname === Paths.home &&
            homeTabSignal.value === 'map'
        )
    }, [mapIsFullscreen, pathname, homeTabSignal.value])

    useEffect(() => {
        if (itineraryMap.length === 0) return

        if (
            !scrollRef ||
            !scrollRef.value ||
            !scrollRef.value.current ||
            !scrollRef.value.current.style
        ) {
            return
        }

        const components =
            scrollRef.value.current.style?.gridTemplateRows?.split(' ')

        components.splice(0, 1, '0px')

        if (expandMap) {
            components.splice(-1, 1, '0px')
        }
        if (scrollRef?.value?.current?.style)
            scrollRef.value.current.style.gridTemplateRows =
                components.join(' ')

        if (!Capacitor.isNativePlatform()) return

        // Correct the status bar colour if it is dark.
        setStatusBarStyleDark()

        // Resets the grid template rows of the Mobile App Frame component to
        // one with a top safe area when the component unmounts.
        return resetTemplate
    }, [carouselObserver?.selectedScrollSnap, expandMap])

    if (expandMap) return null
    if (itineraryMap.length === 0) return null

    return (
        <>
            <CarouselOverlay />
            <CarouselContainer>
                <div
                    style={{
                        width: '100dvw',
                        overflow: 'hidden',
                        height:
                            headerHeight +
                            (windowSizes.value.insets?.insets?.top ?? 0),
                        transition: 'height 0.5s',
                    }}
                    ref={carouselRef}
                >
                    <SlideIterator>
                        {itineraryMap.map((source, index) => (
                            <CarouselSlide
                                key={index}
                                itineraryMap={source}
                                slidesInView={slidesInView}
                                index={index}
                            />
                        ))}
                    </SlideIterator>
                </div>
                <CarouselFooter />
            </CarouselContainer>
        </>
    )
}

function useCachedItineraryMap() {
    const itineraryMap = useBoundStore((state) => state.itineraryMap)
    const [cachedMap, setCachedMap] = useState(itineraryMap)

    useEffect(() => {
        if (itineraryMap !== cachedMap) {
            setCachedMap(itineraryMap)
        }
    }, [itineraryMap, cachedMap])

    return cachedMap
}
